// UniversalProvider.tsx - Fixed type definitions
import React, { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback, 
  useMemo, 
  useRef,
  ReactNode,
  Dispatch,
  SetStateAction
} from 'react';

// ============================================================
// TYPES
// ============================================================

export type ThemeMode = 'light' | 'dark' | 'system';
export type Language = 'en' | 'dv' | 'ar';

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface DialogOptions {
  title?: string;
  description?: string;
  content?: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
    loading?: boolean;
    disabled?: boolean;
  }[];
}

interface FullScreenOptions {
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  enableZoom?: boolean;
  enableFitToScreen?: boolean;
  enableFullscreenToggle?: boolean;
  enablePrint?: boolean;
  enableDownload?: boolean;
  enableCopy?: boolean;
  enableShare?: boolean;
  enableBookmark?: boolean;
  enableExternalLink?: boolean;
  shareData?: { title: string; text: string; url: string };
  copyText?: string;
  downloadContent?: { data: string | Blob; filename: string; type: 'html' | 'text' | 'json' | 'custom'; mimeType?: string };
  isBookmarked?: boolean;
  onBookmarkToggle?: () => void;
  externalLinkUrl?: string;
  loadingMessage?: string;
}

interface ToastOptions {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  duration?: number;
  dismissible?: boolean;
}

interface ModalOptions {
  title?: string;
  content: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
    loading?: boolean;
  }[];
}

// ============================================================
// STORE TYPES
// ============================================================

interface StoreState {
  theme: ThemeMode;
  language: Language;
  isDarkMode: boolean;
  notifications: Notification[];
  toasts: Notification[];
  dialog: {
    isOpen: boolean;
    options: DialogOptions;
  };
  fullscreen: {
    isOpen: boolean;
    content: ReactNode | null;
    options: FullScreenOptions;
    loading: boolean;
    error: string | null;
  };
  modal: {
    isOpen: boolean;
    options: ModalOptions | null;
  };
  loading: {
    [key: string]: boolean;
  };
  data: {
    [key: string]: any;
  };
}

// ============================================================
// CONTEXT
// ============================================================

interface DialogContext {
  isOpen: boolean;
  options: DialogOptions;
  open: (options: DialogOptions) => void;
  close: () => void;
}

interface FullscreenContext {
  isOpen: boolean;
  content: ReactNode | null;
  options: FullScreenOptions;
  loading: boolean;
  error: string | null;
  open: (content: ReactNode, options?: FullScreenOptions) => void;
  close: () => void;
  toggle: () => void;
  updateContent: (content: ReactNode) => void;
  updateOptions: (options: Partial<FullScreenOptions>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

interface ModalContext {
  isOpen: boolean;
  options: ModalOptions | null;
  open: (options: ModalOptions) => void;
  close: () => void;
}

interface UniversalContextValue {
  // Theme
  theme: ThemeMode;
  isDarkMode: boolean;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;

  // Language
  language: Language;
  setLanguage: (lang: Language) => void;

  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Toasts
  toasts: Notification[];
  addToast: (message: string, type?: Notification['type'], options?: ToastOptions) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Dialog - FIXED: Added closeDialog property
  dialog: DialogContext;

  // Fullscreen
  fullscreen: FullscreenContext;

  // Modal
  modal: ModalContext;

  // Loading
  isLoading: (key: string) => boolean;
  setLoading: (key: string, loading: boolean) => void;

  // Data Store
  getData: <T = any>(key: string) => T | undefined;
  setData: <T = any>(key: string, value: T) => void;
  removeData: (key: string) => void;
  clearData: () => void;
}

const UniversalContext = createContext<UniversalContextValue | undefined>(undefined);

// ============================================================
// PROVIDER COMPONENT
// ============================================================

interface UniversalProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
  defaultLanguage?: Language;
  enableThemePersistence?: boolean;
  enableLanguagePersistence?: boolean;
}

export function UniversalProvider({ 
  children, 
  defaultTheme = 'system',
  defaultLanguage = 'en',
  enableThemePersistence = true,
  enableLanguagePersistence = true,
}: UniversalProviderProps) {
  // ============================================================
  // STATE
  // ============================================================

  const [state, setState] = useState<StoreState>(() => {
    // Load persisted state
    let theme = defaultTheme;
    let language = defaultLanguage;
    let isDarkMode = false;

    if (enableThemePersistence) {
      const savedTheme = localStorage.getItem('theme') as ThemeMode | null;
      if (savedTheme) theme = savedTheme;
    }

    if (enableLanguagePersistence) {
      const savedLanguage = localStorage.getItem('language') as Language | null;
      if (savedLanguage) language = savedLanguage;
    }

    // Check system preference
    if (theme === 'system') {
      isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    } else {
      isDarkMode = theme === 'dark';
    }

    return {
      theme,
      language,
      isDarkMode,
      notifications: [],
      toasts: [],
      dialog: {
        isOpen: false,
        options: {},
      },
      fullscreen: {
        isOpen: false,
        content: null,
        options: {},
        loading: false,
        error: null,
      },
      modal: {
        isOpen: false,
        options: null,
      },
      loading: {},
      data: {},
    };
  });

  // ============================================================
  // THEME
  // ============================================================

  const setTheme = useCallback((theme: ThemeMode) => {
    setState(prev => {
      let isDarkMode = false;
      if (theme === 'system') {
        isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      } else {
        isDarkMode = theme === 'dark';
      }

      if (enableThemePersistence) {
        localStorage.setItem('theme', theme);
      }

      // Apply dark mode class
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      return {
        ...prev,
        theme,
        isDarkMode,
      };
    });
  }, [enableThemePersistence]);

  const toggleTheme = useCallback(() => {
    setState(prev => {
      const newTheme = prev.isDarkMode ? 'light' : 'dark';
      if (enableThemePersistence) {
        localStorage.setItem('theme', newTheme);
      }

      if (!prev.isDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      return {
        ...prev,
        theme: newTheme,
        isDarkMode: !prev.isDarkMode,
      };
    });
  }, [enableThemePersistence]);

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (state.theme === 'system') {
        const isDark = e.matches;
        setState(prev => ({
          ...prev,
          isDarkMode: isDark,
        }));
        if (isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [state.theme]);

  // ============================================================
  // LANGUAGE
  // ============================================================

  const setLanguage = useCallback((language: Language) => {
    setState(prev => ({
      ...prev,
      language,
    }));
    if (enableLanguagePersistence) {
      localStorage.setItem('language', language);
    }
  }, [enableLanguagePersistence]);

  // ============================================================
  // NOTIFICATIONS
  // ============================================================

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).slice(2);
    setState(prev => ({
      ...prev,
      notifications: [...prev.notifications, { ...notification, id }],
    }));
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.filter(n => n.id !== id),
    }));
  }, []);

  const clearNotifications = useCallback(() => {
    setState(prev => ({
      ...prev,
      notifications: [],
    }));
  }, []);

  // ============================================================
  // TOASTS
  // ============================================================

  const addToast = useCallback((message: string, type: Notification['type'] = 'info', options: ToastOptions = {}) => {
    const id = Date.now().toString() + Math.random().toString(36).slice(2);
    const duration = options.duration || 5000;
    const toast: Notification = {
      id,
      type,
      message,
      duration,
    };
    setState(prev => ({
      ...prev,
      toasts: [...prev.toasts, toast],
    }));

    // Auto dismiss
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      toasts: prev.toasts.filter(t => t.id !== id),
    }));
  }, []);

  const clearToasts = useCallback(() => {
    setState(prev => ({
      ...prev,
      toasts: [],
    }));
  }, []);

  // ============================================================
  // DIALOG
  // ============================================================

  const openDialog = useCallback((options: DialogOptions) => {
    setState(prev => ({
      ...prev,
      dialog: {
        isOpen: true,
        options,
      },
    }));
  }, []);

  const closeDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      dialog: {
        ...prev.dialog,
        isOpen: false,
      },
    }));
    // Clear options after animation
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        dialog: {
          ...prev.dialog,
          options: {},
        },
      }));
    }, 300);
  }, []);

  // ============================================================
  // FULLSCREEN
  // ============================================================

  const openFullscreen = useCallback((content: ReactNode, options: FullScreenOptions = {}) => {
    setState(prev => ({
      ...prev,
      fullscreen: {
        isOpen: true,
        content,
        options: { ...prev.fullscreen.options, ...options },
        loading: false,
        error: null,
      },
    }));
  }, []);

  const closeFullscreen = useCallback(() => {
    setState(prev => ({
      ...prev,
      fullscreen: {
        ...prev.fullscreen,
        isOpen: false,
      },
    }));
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        fullscreen: {
          ...prev.fullscreen,
          content: null,
        },
      }));
    }, 300);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setState(prev => {
      if (prev.fullscreen.isOpen) {
        closeFullscreen();
        return prev;
      } else {
        return {
          ...prev,
          fullscreen: {
            ...prev.fullscreen,
            isOpen: true,
          },
        };
      }
    });
  }, [closeFullscreen]);

  const updateFullscreenContent = useCallback((content: ReactNode) => {
    setState(prev => ({
      ...prev,
      fullscreen: {
        ...prev.fullscreen,
        content,
      },
    }));
  }, []);

  const updateFullscreenOptions = useCallback((options: Partial<FullScreenOptions>) => {
    setState(prev => ({
      ...prev,
      fullscreen: {
        ...prev.fullscreen,
        options: { ...prev.fullscreen.options, ...options },
      },
    }));
  }, []);

  const setFullscreenLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      fullscreen: {
        ...prev.fullscreen,
        loading,
      },
    }));
  }, []);

  const setFullscreenError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      fullscreen: {
        ...prev.fullscreen,
        error,
      },
    }));
  }, []);

  // ============================================================
  // MODAL
  // ============================================================

  const openModal = useCallback((options: ModalOptions) => {
    setState(prev => ({
      ...prev,
      modal: {
        isOpen: true,
        options,
      },
    }));
  }, []);

  const closeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      modal: {
        ...prev.modal,
        isOpen: false,
      },
    }));
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        modal: {
          ...prev.modal,
          options: null,
        },
      }));
    }, 300);
  }, []);

  // ============================================================
  // LOADING
  // ============================================================

  const isLoading = useCallback((key: string) => {
    return state.loading[key] || false;
  }, [state.loading]);

  const setLoading = useCallback((key: string, loading: boolean) => {
    setState(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        [key]: loading,
      },
    }));
  }, []);

  // ============================================================
  // DATA STORE
  // ============================================================

  const getData = useCallback(<T = any>(key: string): T | undefined => {
    return state.data[key] as T | undefined;
  }, [state.data]);

  const setData = useCallback(<T = any>(key: string, value: T) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value,
      },
    }));
  }, []);

  const removeData = useCallback((key: string) => {
    setState(prev => {
      const { [key]: _, ...rest } = prev.data;
      return {
        ...prev,
        data: rest,
      };
    });
  }, []);

  const clearData = useCallback(() => {
    setState(prev => ({
      ...prev,
      data: {},
    }));
  }, []);

  // ============================================================
  // CONTEXT VALUE
  // ============================================================

  const contextValue = useMemo<UniversalContextValue>(() => ({
    // Theme
    theme: state.theme,
    isDarkMode: state.isDarkMode,
    setTheme,
    toggleTheme,
    
    // Language
    language: state.language,
    setLanguage,
    
    // Notifications
    notifications: state.notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    
    // Toasts
    toasts: state.toasts,
    addToast,
    removeToast,
    clearToasts,
    
    // Dialog
    dialog: {
      isOpen: state.dialog.isOpen,
      options: state.dialog.options,
      open: openDialog,
      close: closeDialog,
    },
    
    // Fullscreen
    fullscreen: {
      isOpen: state.fullscreen.isOpen,
      content: state.fullscreen.content,
      options: state.fullscreen.options,
      loading: state.fullscreen.loading,
      error: state.fullscreen.error,
      open: openFullscreen,
      close: closeFullscreen,
      toggle: toggleFullscreen,
      updateContent: updateFullscreenContent,
      updateOptions: updateFullscreenOptions,
      setLoading: setFullscreenLoading,
      setError: setFullscreenError,
    },
    
    // Modal
    modal: {
      isOpen: state.modal.isOpen,
      options: state.modal.options,
      open: openModal,
      close: closeModal,
    },
    
    // Loading
    isLoading,
    setLoading,
    
    // Data Store
    getData,
    setData,
    removeData,
    clearData,
  }), [
    state,
    setTheme,
    toggleTheme,
    setLanguage,
    addNotification,
    removeNotification,
    clearNotifications,
    addToast,
    removeToast,
    clearToasts,
    openDialog,
    closeDialog,
    openFullscreen,
    closeFullscreen,
    toggleFullscreen,
    updateFullscreenContent,
    updateFullscreenOptions,
    setFullscreenLoading,
    setFullscreenError,
    openModal,
    closeModal,
    isLoading,
    setLoading,
    getData,
    setData,
    removeData,
    clearData,
  ]);

  return (
    <UniversalContext.Provider value={contextValue}>
      {children}
    </UniversalContext.Provider>
  );
}

// ============================================================
// HOOK
// ============================================================

export function useUniversal(): UniversalContextValue {
  const context = useContext(UniversalContext);
  if (!context) {
    throw new Error('useUniversal must be used within a UniversalProvider');
  }
  return context;
}

// ============================================================
// UTILITY COMPONENTS
// ============================================================

// Toast Container
export function ToastContainer() {
  const { toasts, removeToast } = useUniversal();

  if (toasts.length === 0) return null;

  const getToastStyles = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-500 text-white';
      case 'error':
        return 'bg-red-500 text-white';
      case 'warning':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-blue-500 text-white';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`${getToastStyles(toast.type)} px-4 py-3 rounded-lg shadow-lg max-w-sm animate-slideUp`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-white/80 hover:text-white transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Dialog Component
export function Dialog() {
  const { dialog, dialog: { close } } = useUniversal();
  const { isOpen, options } = dialog;

  if (!isOpen) return null;

  const {
    title,
    description,
    content,
    maxWidth = 'md',
    showCloseButton = true,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    actions = [],
  } = options;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeOnBackdropClick ? close : undefined}
      />
      <div
        className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full ${maxWidthClasses[maxWidth]} max-h-[90vh] overflow-y-auto`}
      >
        {showCloseButton && (
          <button
            onClick={close}
            className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            ×
          </button>
        )}
        <div className="p-6">
          {title && (
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
              {title}
            </h2>
          )}
          {description && (
            <p className="text-gray-600 dark:text-gray-300 mb-4">{description}</p>
          )}
          {content && <div className="mb-4">{content}</div>}
          {actions.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2 mt-4">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  disabled={action.disabled || action.loading}
                  className={`
                    px-4 py-2 rounded-lg font-medium transition-all duration-200
                    ${action.variant === 'primary' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                    ${action.variant === 'secondary' ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600' : ''}
                    ${action.variant === 'danger' ? 'bg-red-500 text-white hover:bg-red-600' : ''}
                    ${action.variant === 'success' ? 'bg-green-500 text-white hover:bg-green-600' : ''}
                    ${action.variant === 'warning' ? 'bg-yellow-500 text-white hover:bg-yellow-600' : ''}
                    ${!action.variant ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                    ${(action.disabled || action.loading) ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {action.loading && (
                    <svg className="animate-spin h-4 w-4 inline mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Theme Toggle Button Component
export function ThemeToggle() {
  const { isDarkMode, toggleTheme } = useUniversal();
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      aria-label="Toggle theme"
    >
      {isDarkMode ? '🌙' : '☀️'}
    </button>
  );
}

// Language Selector Component
export function LanguageSelector() {
  const { language, setLanguage } = useUniversal();
  const languages: { code: Language; label: string; flag: string }[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'dv', label: 'Dhivehi', flag: '🇲🇻' },
    { code: 'ar', label: 'Arabic', flag: '🇸🇦' },
  ];

  return (
    <div className="relative inline-block">
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value as Language)}
        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {languages.map(lang => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ============================================================
// EXPORTS
// ============================================================

export default UniversalProvider;